import { useEffect, useRef, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { PhoneNumber } from '../phone/PhoneNumber';
import {
  createOfferte,
  type Brand,
  type CreateOfferteItemInput,
} from '../../lib/offerte-create';
import type { KleedVorm } from '../../lib/lead-product';
import {
  OFFERTE_PRIMARY_PRODUCTS,
  DEFAULT_OFFERTE_PRIMARY_SKU,
  findOffertePrimaryProduct,
} from '../../lib/offerte-products';

/**
 * Vooraf gekozen klant (bv. vanaf de klant-detailpagina): de klantzoek-stap
 * wordt overgeslagen en de velden staan al ingevuld, inclusief account/contact-
 * koppeling zodat de offerte aan het bestaande record hangt.
 */
export interface OffertePrefillCustomer {
  accountId: string;
  isBusiness: boolean;
  name: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  street?: string;
  postalCode?: string;
  city?: string;
  blacklisted?: boolean;
  blacklistReden?: string | null;
}

interface NewOfferteDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (leadId: string) => void;
  /** Optioneel: klant al gekozen — geef een gememoiseerd object door. */
  prefillCustomer?: OffertePrefillCustomer | null;
}

interface CustomerSearchResult {
  id: string;
  type: 'account_person' | 'account_business' | 'lead';
  name: string;
  email: string;
  phone: string;
  city: string;
  postcode: string;
  blacklisted?: boolean;
  blacklistReden?: string | null;
}

interface ContactRow {
  id: string;
  name: string;
  email: string;
  phone: string;
  title: string;
  department: string;
}

const BRAND_OPTIONS: { value: Brand; label: string }[] = [
  { value: 'KLEDA', label: 'Kleda' },
  { value: 'CINCO', label: 'Cinco' },
  { value: 'VLOERKLEEDREINIGEN', label: 'Vloerkleedreinigen.com' },
  { value: 'BANKREINIGEN', label: 'Bankreinigen.nl' },
];

const VORM_OPTIONS: { value: KleedVorm; label: string }[] = [
  { value: 'rechthoekig', label: 'Rechthoekig' },
  { value: 'rond', label: 'Rond' },
  { value: 'overig', label: 'Overig' },
];

interface KleedRow {
  productCode: string;
  vorm: KleedVorm;
  lengte: string;
  breedte: string;
  diameter: string;
  oppervlakte: string;
  aantal: string;
  bijzonderheden: string;
  externeReferentie: string;
}

/** Bovengrens per kopieer-actie; beschermt tegen een tikfout als 290 in het aantal-veld. */
const MAX_KOPIEEN = 100;

/** Het veld is "totaal aantal van dit item": 29 → 28 kopieën naast het origineel; leeg of 1 → één kopie. */
function kopieenUitTotaal(totaal: string): number {
  const n = Math.floor(Number(totaal));
  return n > 1 ? n - 1 : 1;
}

function emptyKleed(): KleedRow {
  return {
    productCode: DEFAULT_OFFERTE_PRIMARY_SKU,
    vorm: 'rechthoekig',
    lengte: '',
    breedte: '',
    diameter: '',
    oppervlakte: '',
    aantal: '1',
    bijzonderheden: '',
    externeReferentie: '',
  };
}

function rowToItem(row: KleedRow): CreateOfferteItemInput | null {
  const product = findOffertePrimaryProduct(row.productCode);
  const isCountBased = product.mode === 'count';

  if (isCountBased) {
    const n = Number(row.aantal);
    if (!(n > 0)) return null;
    const item: CreateOfferteItemInput = {
      productCode: row.productCode,
      aantal: n,
    };
    if (row.bijzonderheden.trim()) item.bijzonderheden = row.bijzonderheden.trim();
    if (row.externeReferentie.trim()) item.externeReferentie = row.externeReferentie.trim();
    return item;
  }

  const item: CreateOfferteItemInput = {
    productCode: row.productCode,
    vorm: row.vorm,
  };
  if (row.vorm === 'rechthoekig') {
    const l = Number(row.lengte);
    const b = Number(row.breedte);
    if (!(l > 0) || !(b > 0)) return null;
    item.lengteCm = l;
    item.breedteCm = b;
  } else if (row.vorm === 'rond') {
    const d = Number(row.diameter);
    if (!(d > 0)) return null;
    item.diameterCm = d;
  } else {
    const m2 = Number(row.oppervlakte);
    if (!(m2 > 0)) return null;
    item.oppervlakteM2 = m2;
  }
  if (row.bijzonderheden.trim()) item.bijzonderheden = row.bijzonderheden.trim();
  if (row.externeReferentie.trim()) item.externeReferentie = row.externeReferentie.trim();
  return item;
}

/**
 * Groepeert opeenvolgende identieke rijen voor de samenvatting (29 × Vloerkleed · 300 × 200 cm).
 * Rijen met een externe referentie blijven los: die is per kleed uniek.
 */
function groupKleden(rows: KleedRow[]): Array<{ row: KleedRow; count: number; firstIdx: number }> {
  const out: Array<{ row: KleedRow; count: number; firstIdx: number }> = [];
  rows.forEach((row, idx) => {
    const last = out[out.length - 1];
    const sameAsLast =
      last &&
      !row.externeReferentie &&
      !last.row.externeReferentie &&
      rowSummary(last.row) === rowSummary(row) &&
      last.row.bijzonderheden.trim() === row.bijzonderheden.trim();
    if (sameAsLast) last.count += 1;
    else out.push({ row, count: 1, firstIdx: idx });
  });
  return out;
}

function CopyIcon() {
  return (
    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
      <rect x="9" y="9" width="11" height="11" rx="2" strokeWidth={2} />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15V6a1 1 0 011-1h9" />
    </svg>
  );
}

function rowSummary(row: KleedRow): string {
  const product = findOffertePrimaryProduct(row.productCode);
  if (product.mode === 'count') {
    const n = Number(row.aantal);
    return n > 0 ? `${product.label} × ${n}` : product.label;
  }
  if (row.vorm === 'rechthoekig' && row.lengte && row.breedte) {
    return `${product.label} · ${row.lengte} × ${row.breedte} cm`;
  }
  if (row.vorm === 'rond' && row.diameter) {
    return `${product.label} · Ø ${row.diameter} cm`;
  }
  if (row.vorm === 'overig' && row.oppervlakte) {
    return `${product.label} · ${row.oppervlakte} m²`;
  }
  return product.label;
}

export function NewOfferteDialog({ isOpen, onClose, onCreated, prefillCustomer }: NewOfferteDialogProps) {
  const [step, setStep] = useState(1);

  // Step 1 — klant
  const [brand, setBrand] = useState<Brand>('KLEDA');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [company, setCompany] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<CustomerSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [pickedCustomerId, setPickedCustomerId] = useState<string | null>(null);
  const [pickedAccountId, setPickedAccountId] = useState<string | null>(null);
  const [pickedContactId, setPickedContactId] = useState<string | null>(null);
  const [accountContacts, setAccountContacts] = useState<ContactRow[] | null>(null);
  const [defaultContactId, setDefaultContactId] = useState<string | null>(null);
  const [loadingContacts, setLoadingContacts] = useState(false);
  // Blacklist van de gekozen klant — informeert de medewerker (server blokkeert
  // de conversie naar order alsnog server-side bij akkoord).
  const [pickedBlacklist, setPickedBlacklist] = useState<{ reden: string | null } | null>(null);

  // Step 2 — adres
  const [street, setStreet] = useState('');
  const [houseNumber, setHouseNumber] = useState('');
  const [houseNumberSuffix, setHouseNumberSuffix] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [city, setCity] = useState('');

  // Step 3 — kleden
  const [kleden, setKleden] = useState<KleedRow[]>([emptyKleed()]);
  const [expandedKleedIdx, setExpandedKleedIdx] = useState<number>(0);
  // Aantal kopieën voor "Kopiëren" (bijv. 29 identieke kleden in één aanvraag).
  const [kopieAantal, setKopieAantal] = useState<string>('1');

  // Step 4 — optionele korting op de hele offerte (percentage als string-invoer)
  const [kortingPercentage, setKortingPercentage] = useState('');

  // Submit
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const searchAbortRef = useRef<AbortController | null>(null);

  // Reset bij openen
  useEffect(() => {
    if (!isOpen) return;
    setStep(1);
    setBrand('KLEDA');
    setFirstName('');
    setLastName('');
    setEmail('');
    setPhone('');
    setCompany('');
    setSearchQuery('');
    setSearchResults([]);
    setPickedCustomerId(null);
    setPickedAccountId(null);
    setPickedContactId(null);
    setAccountContacts(null);
    setDefaultContactId(null);
    setLoadingContacts(false);
    setPickedBlacklist(null);
    setStreet('');
    setHouseNumber('');
    setHouseNumberSuffix('');
    setPostalCode('');
    setCity('');
    setKleden([emptyKleed()]);
    setExpandedKleedIdx(0);
    setKortingPercentage('');
    setSubmitting(false);
    setSubmitError(null);

    // Prefill (klant-detailpagina): zelfde pad als pickCustomer, zonder zoeken.
    if (!prefillCustomer) return;
    const p = prefillCustomer;
    setPickedCustomerId(p.accountId);
    setPickedAccountId(p.accountId);
    setPickedBlacklist(p.blacklisted ? { reden: p.blacklistReden ?? null } : null);
    if (p.street) setStreet(p.street);
    if (p.postalCode) setPostalCode(p.postalCode);
    if (p.city) setCity(p.city);

    if (p.isBusiness) {
      // B2B: bedrijfsnaam vullen; naam/email/phone volgen uit de contact-keuze.
      setCompany(p.name);
      void fetchAccountContacts(p.accountId);
      return;
    }

    // PersonAccount: naamvelden direct uit SF waar aanwezig, anders splitsen.
    if (p.firstName || p.lastName) {
      setFirstName(p.firstName ?? '');
      setLastName(p.lastName || p.name);
    } else {
      const parts = p.name.trim().split(/\s+/).filter(Boolean);
      setFirstName(parts.length > 1 ? parts[0] ?? '' : '');
      setLastName(parts.length > 1 ? parts.slice(1).join(' ') : (parts[0] ?? p.name));
    }
    setEmail(p.email ?? '');
    setPhone(p.phone ?? '');
    // PersonContactId ophalen zodat het contact direct gekoppeld wordt.
    void (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const headers: Record<string, string> = {};
        if (session?.access_token) headers['Authorization'] = `Bearer ${session.access_token}`;
        const response = await fetch(
          `/api/customers/contacts-by-account?id=${encodeURIComponent(p.accountId)}`,
          { headers }
        );
        if (!response.ok) return;
        const data = (await response.json()) as { defaultContactId?: string | null };
        if (data.defaultContactId) setPickedContactId(data.defaultContactId);
      } catch {
        // Geen ID → fallback op AccountResolverService strong-match.
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, prefillCustomer]);

  // Customer search debounced
  useEffect(() => {
    const trimmed = searchQuery.trim();
    if (trimmed.length < 2) {
      setSearchResults([]);
      setSearching(false);
      return;
    }

    if (searchAbortRef.current) searchAbortRef.current.abort();
    const controller = new AbortController();
    searchAbortRef.current = controller;
    setSearching(true);

    const timer = setTimeout(async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const headers: Record<string, string> = {};
        if (session?.access_token) headers['Authorization'] = `Bearer ${session.access_token}`;
        const response = await fetch(
          `/api/customers/search?q=${encodeURIComponent(trimmed)}`,
          { headers, signal: controller.signal }
        );
        if (!response.ok) {
          setSearchResults([]);
          return;
        }
        const data = await response.json();
        setSearchResults(((data.results as CustomerSearchResult[]) || []).slice(0, 8));
      } catch (err) {
        if ((err as { name?: string })?.name === 'AbortError') return;
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [searchQuery]);

  // Auto-preselect contact in the account-contacts list:
  // - defaultContactId (Account.Standaard_Contact_Werk_Order__c) if present
  // - else first contact in the list
  useEffect(() => {
    if (!accountContacts || accountContacts.length === 0) return;
    if (pickedContactId) return;
    const preferred = defaultContactId
      ? accountContacts.find((c) => c.id === defaultContactId)
      : null;
    const target = preferred ?? accountContacts[0];
    if (!target) return;
    const parts = target.name.trim().split(/\s+/).filter(Boolean);
    if (parts.length <= 1) {
      setFirstName('');
      setLastName(parts[0] ?? target.name.trim());
    } else {
      setFirstName(parts[0] ?? '');
      setLastName(parts.slice(1).join(' '));
    }
    if (target.email) setEmail(target.email);
    if (target.phone) setPhone(target.phone);
    setPickedContactId(target.id);
  }, [accountContacts, defaultContactId, pickedContactId]);

  if (!isOpen) return null;

  async function fetchAccountContacts(accountId: string) {
    setLoadingContacts(true);
    setAccountContacts(null);
    setDefaultContactId(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const headers: Record<string, string> = {};
      if (session?.access_token) headers['Authorization'] = `Bearer ${session.access_token}`;
      const response = await fetch(
        `/api/customers/contacts-by-account?id=${encodeURIComponent(accountId)}`,
        { headers }
      );
      if (!response.ok) {
        setAccountContacts([]);
        return;
      }
      const data = (await response.json()) as { contacts?: ContactRow[]; defaultContactId?: string | null };
      setAccountContacts(data.contacts ?? []);
      setDefaultContactId(data.defaultContactId ?? null);
    } catch {
      setAccountContacts([]);
    } finally {
      setLoadingContacts(false);
    }
  }

  function applyContactToFields(c: ContactRow) {
    const parts = c.name.trim().split(/\s+/).filter(Boolean);
    if (parts.length <= 1) {
      setFirstName('');
      setLastName(parts[0] ?? c.name.trim());
    } else {
      setFirstName(parts[0] ?? '');
      setLastName(parts.slice(1).join(' '));
    }
    if (c.email) setEmail(c.email);
    if (c.phone) setPhone(c.phone);
    setPickedContactId(c.id);
  }

  function pickCustomer(r: CustomerSearchResult) {
    setSearchQuery('');
    setSearchResults([]);
    setPickedCustomerId(r.id);
    setPickedBlacklist(r.blacklisted ? { reden: r.blacklistReden ?? null } : null);

    if (r.type === 'account_business') {
      // B2B: bedrijfsnaam vullen, naam/email/phone wachten op contact-pick.
      setCompany(r.name);
      setFirstName('');
      setLastName('');
      setEmail('');
      setPhone('');
      setCity(r.city || city);
      setPostalCode(r.postcode || postalCode);
      setPickedAccountId(r.id);
      setPickedContactId(null);
      void fetchAccountContacts(r.id);
      return;
    }

    if (r.type === 'account_person') {
      // PersonAccount: naam splitsen, IDs ophalen via contacts-by-account
      // (defaultContactId = PersonContactId zodat contact direct gekoppeld is).
      const parts = r.name.trim().split(/\s+/).filter(Boolean);
      if (parts.length <= 1) {
        setFirstName('');
        setLastName(parts[0] ?? r.name.trim());
      } else {
        setFirstName(parts[0] ?? '');
        setLastName(parts.slice(1).join(' '));
      }
      setEmail(r.email || '');
      setPhone(r.phone || '');
      setCity(r.city || city);
      setPostalCode(r.postcode || postalCode);
      setCompany('');
      setPickedAccountId(r.id);
      setPickedContactId(null);
      setAccountContacts(null);
      // Haal PersonContactId op via endpoint (defaultContactId).
      void (async () => {
        try {
          const { data: { session } } = await supabase.auth.getSession();
          const headers: Record<string, string> = {};
          if (session?.access_token) headers['Authorization'] = `Bearer ${session.access_token}`;
          const response = await fetch(
            `/api/customers/contacts-by-account?id=${encodeURIComponent(r.id)}`,
            { headers }
          );
          if (!response.ok) return;
          const data = (await response.json()) as { defaultContactId?: string | null };
          if (data.defaultContactId) setPickedContactId(data.defaultContactId);
        } catch {
          // Geen ID → fallback op AccountResolverService strong-match.
        }
      })();
      return;
    }

    // Lead: oude logica, geen IDs zetten.
    const parts = r.name.trim().split(/\s+/).filter(Boolean);
    if (parts.length <= 1) {
      setFirstName('');
      setLastName(parts[0] ?? r.name.trim());
    } else {
      setFirstName(parts[0] ?? '');
      setLastName(parts.slice(1).join(' '));
    }
    setEmail(r.email || '');
    setPhone(r.phone || '');
    setCity(r.city || city);
    setPostalCode(r.postcode || postalCode);
    setPickedAccountId(null);
    setPickedContactId(null);
    setAccountContacts(null);
  }

  function clearPickedCustomer() {
    setPickedCustomerId(null);
    setPickedAccountId(null);
    setPickedContactId(null);
    setAccountContacts(null);
    setDefaultContactId(null);
    setPickedBlacklist(null);
  }


  function addKleed() {
    const newIdx = kleden.length;
    setKleden((prev) => [...prev, emptyKleed()]);
    setExpandedKleedIdx(newIdx);
  }

  /** Voegt `count` kopieën van rij `index` direct ná die rij in (externe referentie blijft leeg: die is per kleed uniek). */
  function copyKleed(index: number, count: number) {
    const n = Math.max(1, Math.min(MAX_KOPIEEN, Math.floor(count)));
    setKleden((prev) => {
      const src = prev[index];
      if (!src) return prev;
      const copies = Array.from({ length: n }, () => ({ ...src, externeReferentie: '' }));
      return [...prev.slice(0, index + 1), ...copies, ...prev.slice(index + 1)];
    });
    setExpandedKleedIdx(index + n);
    setKopieAantal('1');
  }

  function removeKleed(index: number) {
    const remaining = kleden.length - 1;
    setKleden((prev) => prev.filter((_, i) => i !== index));
    // Houd expanded-index binnen de bounds; default = laatste resterende rij.
    setExpandedKleedIdx(Math.max(0, Math.min(index, remaining - 1)));
  }

  function updateKleed(index: number, patch: Partial<KleedRow>) {
    setKleden((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  }

  // Validation — adres is optioneel; office kan dit later op detail-page invullen.
  // B2B-pick (account_business gekozen → pickedAccountId gezet) eist een
  // contact-keuze, anders weet Apex niet welke Contact te koppelen.
  const needsContactPick = pickedAccountId !== null && accountContacts !== null && accountContacts.length > 0;
  const contactPicked = pickedContactId !== null;
  const step1Valid =
    lastName.trim().length > 0 &&
    (email.trim().length > 0 || phone.trim().length > 0) &&
    (!needsContactPick || contactPicked);
  const step2Valid = true;
  const step3Valid = kleden.length > 0 && kleden.every((row) => rowToItem(row) !== null);
  const allValid = step1Valid && step3Valid;

  async function handleSubmit() {
    if (!allValid) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const items = kleden
        .map((row) => rowToItem(row))
        .filter((item): item is CreateOfferteItemInput => item !== null);

      const composedStreet = [street.trim(), houseNumber.trim()].filter(Boolean).join(' ').trim();
      const hasAddress =
        composedStreet || postalCode.trim() || city.trim() || houseNumberSuffix.trim();

      const kortingRaw = kortingPercentage.trim().replace(',', '.');
      const kortingParsed = kortingRaw ? Number.parseFloat(kortingRaw) : NaN;
      const korting =
        Number.isFinite(kortingParsed) && kortingParsed > 0 ? kortingParsed : null;

      const res = await createOfferte({
        customer: {
          firstName: firstName.trim() || null,
          lastName: lastName.trim(),
          email: email.trim() || null,
          phone: phone.trim() || null,
          company: company.trim() || null,
          existingAccountId: pickedAccountId,
          existingContactId: pickedContactId,
        },
        address: hasAddress
          ? {
              street: composedStreet || null,
              houseNumberSuffix: houseNumberSuffix.trim() || null,
              postalCode: postalCode.trim() || null,
              city: city.trim() || null,
            }
          : null,
        brand,
        marketingChannel: 'PHONE',
        items,
        bron: 'Office-App',
        kortingPercentage: korting,
      });

      if (!res.success || !res.leadId) {
        setSubmitError(res.errorMessage || res.errorCode || 'Aanmaken mislukt');
        return;
      }
      onCreated(res.leadId);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Onverwachte fout');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
        <div className="px-5 py-4 border-b border-slate-200 flex items-start justify-between gap-4">
          <div>
            <h3 className="text-base font-semibold text-slate-900">Nieuwe offerte</h3>
            <p className="text-xs text-slate-500 mt-0.5">Stap {step} van 4</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 flex-shrink-0"
            aria-label="Sluiten"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Step indicator */}
        <div className="px-5 pt-3">
          <div className="flex gap-1">
            {[1, 2, 3, 4].map((n) => (
              <div
                key={n}
                className={
                  'flex-1 h-1 rounded-full ' +
                  (n <= step ? 'bg-indigo-600' : 'bg-slate-200')
                }
              />
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {step === 1 && (
            <div className="space-y-4">
              <div>
                <span className="text-xs font-medium text-slate-700">Merk</span>
                <div className="mt-1 flex flex-wrap gap-2">
                  {BRAND_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setBrand(opt.value)}
                      className={
                        'px-3 py-1.5 text-xs rounded-md border ' +
                        (brand === opt.value
                          ? 'bg-indigo-600 border-indigo-600 text-white'
                          : 'bg-white border-slate-300 text-slate-700 hover:bg-slate-50')
                      }
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block">
                  <span className="text-xs font-medium text-slate-700">Bestaande klant zoeken (optioneel)</span>
                  <input
                    type="search"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Naam, email, telefoon, postcode…"
                    className="mt-1 w-full px-3 py-1.5 border border-slate-300 rounded-md text-sm"
                  />
                </label>
                {(searching || searchResults.length > 0) && (
                  <div className="mt-2 border border-slate-200 rounded-md bg-white max-h-56 overflow-y-auto">
                    {searching && (
                      <div className="px-3 py-2 text-xs text-slate-500">Zoeken…</div>
                    )}
                    {!searching && searchResults.length === 0 && searchQuery.trim().length >= 2 && (
                      <div className="px-3 py-2 text-xs text-slate-500">Geen resultaten</div>
                    )}
                    {searchResults.map((r) => (
                      <button
                        key={`${r.type}-${r.id}`}
                        type="button"
                        onClick={() => pickCustomer(r)}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-indigo-50 border-b border-slate-100 last:border-b-0"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-medium text-slate-900 flex items-center gap-1.5">
                            {r.name}
                            {r.blacklisted && (
                              <span
                                className="text-[10px] px-1.5 py-0.5 rounded font-semibold bg-red-100 text-red-700"
                                title={r.blacklistReden || 'Klant staat op de blacklist'}
                              >
                                ⛔ Blacklist
                              </span>
                            )}
                          </span>
                          <span className="text-[10px] uppercase tracking-wide text-slate-500 flex-shrink-0">
                            {r.type === 'lead'
                              ? 'Lead'
                              : r.type === 'account_business'
                              ? 'Zakelijk'
                              : 'Particulier'}
                          </span>
                        </div>
                        <div className="text-xs text-slate-500 mt-0.5 truncate">
                          {[r.email, r.phone, r.city].filter(Boolean).join(' · ')}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
                {pickedCustomerId && (
                  <div className="mt-2 flex items-center justify-between rounded-md bg-emerald-50 border border-emerald-200 px-3 py-1.5 text-xs text-emerald-800">
                    <span>Klantgegevens overgenomen — pas aan indien nodig.</span>
                    <button
                      type="button"
                      onClick={clearPickedCustomer}
                      className="text-emerald-700 hover:underline"
                    >
                      Wissen
                    </button>
                  </div>
                )}
                {pickedBlacklist && (
                  <div className="mt-2 rounded-md bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-800">
                    <span className="font-semibold">⛔ Deze klant staat op de blacklist.</span>{' '}
                    Een order/offerte voor deze klant wordt bij akkoord geweigerd.
                    {pickedBlacklist.reden && (
                      <span className="block mt-0.5 text-red-700">Reden: {pickedBlacklist.reden}</span>
                    )}
                  </div>
                )}
                {pickedAccountId && (loadingContacts || (accountContacts !== null && accountContacts.length > 0)) && (
                  <div className="mt-2">
                    <div className="text-[11px] font-medium text-slate-700 mb-1">
                      Contactpersoon kiezen *
                    </div>
                    <div className="border border-slate-200 rounded-md bg-white max-h-44 overflow-y-auto">
                      {loadingContacts && (
                        <div className="px-3 py-2 text-xs text-slate-500">Contacten laden…</div>
                      )}
                      {!loadingContacts && accountContacts && accountContacts.length === 0 && (
                        <div className="px-3 py-2 text-xs text-amber-700">
                          Geen contactpersonen op dit account. Maak er eerst één aan in Salesforce.
                        </div>
                      )}
                      {!loadingContacts && accountContacts && accountContacts.map((c) => {
                        const isSelected = c.id === pickedContactId;
                        const isDefault = c.id === defaultContactId;
                        return (
                          <button
                            key={c.id}
                            type="button"
                            onClick={() => applyContactToFields(c)}
                            className={
                              'w-full text-left px-3 py-2 text-sm border-b border-slate-100 last:border-b-0 ' +
                              (isSelected ? 'bg-indigo-50' : 'hover:bg-slate-50')
                            }
                          >
                            <div className="flex items-center justify-between gap-2">
                              <span className={isSelected ? 'font-medium text-indigo-900' : 'font-medium text-slate-900'}>
                                {c.name}
                              </span>
                              <div className="flex items-center gap-1">
                                {isDefault && (
                                  <span className="text-[9px] uppercase tracking-wide bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">
                                    Standaard
                                  </span>
                                )}
                                {isSelected && (
                                  <svg className="w-4 h-4 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                )}
                              </div>
                            </div>
                            {(c.email || c.phone || c.title) && (
                              <div className="text-xs text-slate-500 mt-0.5 truncate">
                                {[c.title, c.email].filter(Boolean).join(' · ')}
                                {c.phone && (c.title || c.email) ? ' · ' : ''}
                                {c.phone && <PhoneNumber number={c.phone} sourceContext="new-offerte-search" stopPropagation />}
                              </div>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-xs text-slate-600">Voornaam</span>
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="mt-1 w-full px-3 py-1.5 border border-slate-300 rounded-md text-sm"
                  />
                </label>
                <label className="block">
                  <span className="text-xs text-slate-600">Achternaam *</span>
                  <input
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="mt-1 w-full px-3 py-1.5 border border-slate-300 rounded-md text-sm"
                  />
                </label>
              </div>
              <label className="block">
                <span className="text-xs text-slate-600">Bedrijfsnaam (optioneel)</span>
                <input
                  type="text"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  placeholder="Bijv. Hendrik de Keyser"
                  className="mt-1 w-full px-3 py-1.5 border border-slate-300 rounded-md text-sm"
                />
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-xs text-slate-600">Email</span>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="mt-1 w-full px-3 py-1.5 border border-slate-300 rounded-md text-sm"
                  />
                </label>
                <label className="block">
                  <span className="text-xs text-slate-600">Telefoon</span>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="mt-1 w-full px-3 py-1.5 border border-slate-300 rounded-md text-sm"
                  />
                </label>
              </div>
              <p className="text-[11px] text-slate-400">
                * Achternaam en email óf telefoon zijn verplicht.
              </p>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <label className="block col-span-2">
                  <span className="text-xs text-slate-600">Straat</span>
                  <input
                    type="text"
                    value={street}
                    onChange={(e) => setStreet(e.target.value)}
                    className="mt-1 w-full px-3 py-1.5 border border-slate-300 rounded-md text-sm"
                  />
                </label>
                <label className="block">
                  <span className="text-xs text-slate-600">Huisnr.</span>
                  <input
                    type="text"
                    value={houseNumber}
                    onChange={(e) => setHouseNumber(e.target.value)}
                    className="mt-1 w-full px-3 py-1.5 border border-slate-300 rounded-md text-sm"
                  />
                </label>
              </div>
              <label className="block">
                <span className="text-xs text-slate-600">Toevoeging (optioneel)</span>
                <input
                  type="text"
                  value={houseNumberSuffix}
                  onChange={(e) => setHouseNumberSuffix(e.target.value)}
                  placeholder="bijv. A, bis, II"
                  className="mt-1 w-full px-3 py-1.5 border border-slate-300 rounded-md text-sm"
                />
              </label>
              <div className="grid grid-cols-3 gap-3">
                <label className="block">
                  <span className="text-xs text-slate-600">Postcode</span>
                  <input
                    type="text"
                    value={postalCode}
                    onChange={(e) => setPostalCode(e.target.value.toUpperCase())}
                    placeholder="1234AB"
                    maxLength={7}
                    className="mt-1 w-full px-3 py-1.5 border border-slate-300 rounded-md text-sm"
                  />
                </label>
                <label className="block col-span-2">
                  <span className="text-xs text-slate-600">Plaats</span>
                  <input
                    type="text"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    className="mt-1 w-full px-3 py-1.5 border border-slate-300 rounded-md text-sm"
                  />
                </label>
              </div>
              <p className="text-[11px] text-slate-400">
                Adres is optioneel — kan later via de detail-page worden aangevuld.
              </p>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-3">
              {kleden.map((row, idx) => {
                const isExpanded = expandedKleedIdx === idx;
                const rowComplete = rowToItem(row) !== null;
                const product = findOffertePrimaryProduct(row.productCode);
                const isCountBased = product.mode === 'count';
                if (!isExpanded) {
                  return (
                    <div
                      key={idx}
                      className="w-full border border-slate-200 rounded-lg bg-white hover:bg-slate-50 flex items-center gap-1"
                    >
                    <button
                      type="button"
                      onClick={() => setExpandedKleedIdx(idx)}
                      className="flex-1 min-w-0 px-3 py-2.5 flex items-center justify-between gap-3 text-left"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <span
                          className={
                            'inline-flex w-5 h-5 rounded-full items-center justify-center text-[10px] font-medium flex-shrink-0 ' +
                            (rowComplete ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700')
                          }
                        >
                          {idx + 1}
                        </span>
                        <span className="text-sm font-medium text-slate-800 truncate">
                          {rowSummary(row)}
                        </span>
                        {row.externeReferentie && (
                          <span className="text-xs text-slate-500 truncate">
                            · ref. {row.externeReferentie}
                          </span>
                        )}
                      </div>
                      <svg
                        className="w-4 h-4 text-slate-400 flex-shrink-0"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {rowComplete && (
                      <button
                        type="button"
                        onClick={() => copyKleed(idx, 1)}
                        title="Item kopiëren"
                        aria-label="Item kopiëren"
                        className="mr-2 p-1 rounded text-slate-400 hover:text-indigo-600 hover:bg-indigo-50"
                      >
                        <CopyIcon />
                      </button>
                    )}
                    </div>
                  );
                }
                return (
                <div
                  key={idx}
                  className="border border-slate-200 rounded-lg p-3 space-y-3 bg-slate-50/50"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-medium text-slate-700">Item {idx + 1}</span>
                    <div className="flex items-center gap-3">
                      {rowComplete && (
                        <div className="flex items-center gap-1" title="Totaal aantal van dit item (bijv. 29 dezelfde kleden): het item wordt zo vaak gekopieerd">
                          <input
                            type="number"
                            inputMode="numeric"
                            min={1}
                            max={MAX_KOPIEEN}
                            value={kopieAantal}
                            onChange={(e) => setKopieAantal(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                copyKleed(idx, kopieenUitTotaal(kopieAantal));
                              }
                            }}
                            aria-label="Totaal aantal van dit item"
                            className="w-12 px-1.5 py-0.5 border border-slate-300 rounded-md text-xs text-center bg-white"
                          />
                          <button
                            type="button"
                            onClick={() => copyKleed(idx, kopieenUitTotaal(kopieAantal))}
                            className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:underline"
                          >
                            <CopyIcon />
                            {Number(kopieAantal) > 1 ? `Maak er ${Number(kopieAantal)} van` : 'Kopiëren'}
                          </button>
                        </div>
                      )}
                      {kleden.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeKleed(idx)}
                          className="text-xs text-red-600 hover:underline"
                        >
                          Verwijderen
                        </button>
                      )}
                    </div>
                  </div>

                  <label className="block">
                    <span className="text-[11px] text-slate-600">Product</span>
                    <select
                      value={row.productCode}
                      onChange={(e) => updateKleed(idx, { productCode: e.target.value })}
                      className="mt-1 w-full px-3 py-1.5 border border-slate-300 rounded-md text-sm bg-white"
                    >
                      {OFFERTE_PRIMARY_PRODUCTS.map((p) => (
                        <option key={p.sku} value={p.sku}>
                          {p.label}
                        </option>
                      ))}
                    </select>
                  </label>

                  {isCountBased ? (
                    <label className="block">
                      <span className="text-[11px] text-slate-600">Aantal (stuks)</span>
                      <input
                        type="number"
                        inputMode="numeric"
                        min={1}
                        value={row.aantal}
                        onChange={(e) => updateKleed(idx, { aantal: e.target.value })}
                        className="mt-1 w-full px-3 py-1.5 border border-slate-300 rounded-md text-sm"
                      />
                    </label>
                  ) : (
                    <>
                      <div>
                        <span className="text-[11px] text-slate-600">Vorm</span>
                        <div className="mt-1 inline-flex rounded-md border border-slate-300 overflow-hidden">
                          {VORM_OPTIONS.map((opt, i) => (
                            <button
                              key={opt.value}
                              type="button"
                              onClick={() => updateKleed(idx, { vorm: opt.value })}
                              className={
                                'px-3 py-1.5 text-xs ' +
                                (row.vorm === opt.value
                                  ? 'bg-indigo-600 text-white'
                                  : 'bg-white text-slate-700 hover:bg-slate-50') +
                                (i > 0 ? ' border-l border-slate-300' : '')
                              }
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      {row.vorm === 'rechthoekig' && (
                        <div className="grid grid-cols-2 gap-3">
                          <label className="block">
                            <span className="text-[11px] text-slate-600">Lengte (cm)</span>
                            <input
                              type="number"
                              inputMode="numeric"
                              min={1}
                              value={row.lengte}
                              onChange={(e) => updateKleed(idx, { lengte: e.target.value })}
                              className="mt-1 w-full px-3 py-1.5 border border-slate-300 rounded-md text-sm"
                            />
                          </label>
                          <label className="block">
                            <span className="text-[11px] text-slate-600">Breedte (cm)</span>
                            <input
                              type="number"
                              inputMode="numeric"
                              min={1}
                              value={row.breedte}
                              onChange={(e) => updateKleed(idx, { breedte: e.target.value })}
                              className="mt-1 w-full px-3 py-1.5 border border-slate-300 rounded-md text-sm"
                            />
                          </label>
                        </div>
                      )}

                      {row.vorm === 'rond' && (
                        <label className="block">
                          <span className="text-[11px] text-slate-600">Diameter (cm)</span>
                          <input
                            type="number"
                            inputMode="numeric"
                            min={1}
                            value={row.diameter}
                            onChange={(e) => updateKleed(idx, { diameter: e.target.value })}
                            className="mt-1 w-full px-3 py-1.5 border border-slate-300 rounded-md text-sm"
                          />
                        </label>
                      )}

                      {row.vorm === 'overig' && (
                        <label className="block">
                          <span className="text-[11px] text-slate-600">Oppervlakte (m²)</span>
                          <input
                            type="number"
                            inputMode="decimal"
                            min={0.01}
                            step={0.01}
                            value={row.oppervlakte}
                            onChange={(e) => updateKleed(idx, { oppervlakte: e.target.value })}
                            className="mt-1 w-full px-3 py-1.5 border border-slate-300 rounded-md text-sm"
                          />
                        </label>
                      )}
                    </>
                  )}

                  <label className="block">
                    <span className="text-[11px] text-slate-600">Bijzonderheden (optioneel)</span>
                    <textarea
                      rows={2}
                      maxLength={500}
                      value={row.bijzonderheden}
                      onChange={(e) => updateKleed(idx, { bijzonderheden: e.target.value })}
                      placeholder="Bijv. randen iets gerafeld"
                      className="mt-1 w-full px-3 py-1.5 border border-slate-300 rounded-md text-sm"
                    />
                  </label>

                  <label className="block">
                    <span className="text-[11px] text-slate-600">Externe referentie (optioneel)</span>
                    <input
                      type="text"
                      maxLength={80}
                      value={row.externeReferentie}
                      onChange={(e) => updateKleed(idx, { externeReferentie: e.target.value })}
                      placeholder="Bijv. museum-objectnr 002.523, claimnummer"
                      className="mt-1 w-full px-3 py-1.5 border border-slate-300 rounded-md text-sm"
                    />
                  </label>
                </div>
                );
              })}
              <button
                type="button"
                onClick={addKleed}
                className="w-full text-sm px-3 py-2 rounded-md border border-dashed border-slate-300 text-slate-600 hover:bg-slate-50"
              >
                + Item toevoegen
              </button>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-4 text-sm">
              {submitError && (
                <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700">
                  {submitError}
                </div>
              )}
              <section>
                <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Klant</h4>
                <div className="mt-1 text-slate-900">
                  {[firstName, lastName].filter(Boolean).join(' ')}
                  {company && <span className="text-slate-500"> · {company}</span>}
                </div>
                <div className="text-slate-600 text-xs">
                  {[email, phone].filter(Boolean).join(' · ')}
                </div>
                <div className="text-slate-500 text-xs mt-0.5">Merk: {brand}</div>
              </section>
              <section>
                <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Adres</h4>
                <div className="mt-1 text-slate-900">
                  {[street, houseNumber].filter(Boolean).join(' ')}
                  {houseNumberSuffix && ` ${houseNumberSuffix}`}
                </div>
                <div className="text-slate-600 text-xs">
                  {[postalCode, city].filter(Boolean).join(' ')}
                </div>
              </section>
              <section>
                <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                  {kleden.length} {kleden.length === 1 ? 'item' : 'items'}
                </h4>
                <ul className="mt-1 divide-y divide-slate-100 border border-slate-200 rounded-md overflow-hidden">
                  {groupKleden(kleden).map((g, gi) => (
                    <li key={gi} className="px-3 py-2 text-xs text-slate-700">
                      <div className="flex justify-between">
                        <span>
                          {g.count > 1 ? `${g.count} × ` : `${g.firstIdx + 1}. `}
                          {rowSummary(g.row)}
                        </span>
                        {g.row.externeReferentie && (
                          <span className="text-slate-500">ref. {g.row.externeReferentie}</span>
                        )}
                      </div>
                      {g.row.bijzonderheden && (
                        <div className="text-slate-500 mt-0.5 truncate">{g.row.bijzonderheden}</div>
                      )}
                    </li>
                  ))}
                </ul>
              </section>
              <section>
                <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Korting</h4>
                <div className="mt-1 flex items-center gap-2">
                  <input
                    type="text"
                    inputMode="decimal"
                    value={kortingPercentage}
                    onChange={(e) => setKortingPercentage(e.target.value)}
                    placeholder="0"
                    className="w-24 px-3 py-1.5 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                  <span className="text-sm text-slate-500">% korting op de hele offerte</span>
                </div>
                <p className="text-[11px] text-slate-400 mt-1">
                  Optioneel. Laat leeg voor geen korting. Achteraf aanpasbaar op de offerte-detail page.
                </p>
              </section>
              <p className="text-[11px] text-slate-400">
                Pakket-keuze, prijzen en eventuele extra behandelingen kun je toevoegen op de offerte-detail page nadat de offerte is aangemaakt.
              </p>
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-200 flex justify-between gap-2">
          <button
            type="button"
            onClick={onClose}
            className="text-sm px-3 py-1.5 rounded-md text-slate-600 hover:bg-slate-100"
          >
            Annuleren
          </button>
          <div className="flex gap-2">
            {step > 1 && (
              <button
                type="button"
                onClick={() => setStep((s) => s - 1)}
                className="text-sm px-3 py-1.5 rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50"
              >
                Vorige
              </button>
            )}
            {step < 4 && (
              <button
                type="button"
                onClick={() => setStep((s) => s + 1)}
                disabled={
                  (step === 1 && !step1Valid) ||
                  (step === 2 && !step2Valid) ||
                  (step === 3 && !step3Valid)
                }
                className="text-sm px-3 py-1.5 rounded-md bg-indigo-600 text-white font-medium hover:bg-indigo-700 disabled:bg-slate-300"
              >
                Volgende
              </button>
            )}
            {step === 4 && (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={submitting || !allValid}
                className="text-sm px-3 py-1.5 rounded-md bg-indigo-600 text-white font-medium hover:bg-indigo-700 disabled:bg-slate-300"
              >
                {submitting ? 'Aanmaken…' : 'Offerte aanmaken'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
